import React from 'react';
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    TextField,
    Grid,
    Typography,
    Tooltip,
    IconButton,
    Accordion,
    AccordionSummary,
    AccordionDetails,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Paper
} from '@mui/material';
import { Info as InfoIcon, ExpandMore as ExpandMoreIcon } from '@mui/icons-material';
import { Company, CompanyBenefitsConfig, CompanyTaxConfig } from '../../types/company';
import { defaultBenefitsConfig, defaultTaxConfig } from '../../config/defaultCompanyConfig';
import api from '../../services/api';

interface CompanyConfigDialogProps {
    open: boolean;
    company: Company;
    onClose: () => void;
    onSave: (company: Company) => void;
}

const benefitsTooltips = {
    valeTransporte: 'Valor diário do Vale Transporte por funcionário',
    valeRefeicao: 'Valor diário do Vale Refeição por funcionário',
    valeAlimentacao: 'Valor diário do Vale Alimentação por funcionário',
    auxilioSaude: 'Valor mensal do Auxílio Saúde por funcionário',
    diasUteis: 'Média de dias úteis por mês para cálculo dos benefícios',
    descontoVT: 'Percentual de desconto do Vale Transporte sobre o salário (máximo 6%)'
};

const taxTooltips = {
    inssPatronal: 'Contribuição da empresa para o INSS (20% sobre a folha)',
    ratSat: 'Risco Ambiental do Trabalho (varia de 1% a 3%)',
    inssTerceiros: 'Contribuições para terceiros (Sistema S)',
    salarioEducacao: 'Contribuição para o Salário Educação (2,5%)',
    fgts: 'Fundo de Garantia do Tempo de Serviço (8%)'
};

export const CompanyConfigDialog: React.FC<CompanyConfigDialogProps> = ({
    open,
    company,
    onClose,
    onSave
}) => {
    const [benefits, setBenefits] = React.useState<CompanyBenefitsConfig>(
        company?.benefits || defaultBenefitsConfig
    );
    const [taxConfig, setTaxConfig] = React.useState<CompanyTaxConfig>(
        company?.tax_config || defaultTaxConfig
    );

    const handleSave = async () => {
        try {
            const updatedCompany = {
                ...company,
                benefits,
                tax_config: taxConfig
            };
            
            await api.put(`/api/companies/${company.id}/config`, {
                benefits,
                tax_config: taxConfig
            });

            onSave(updatedCompany);
        } catch (error) {
            console.error('Error updating company config:', error);
            // TODO: Add error handling
        }
    };

    const renderTooltip = (text: string) => (
        <Tooltip title={text}>
            <IconButton size="small">
                <InfoIcon fontSize="small" />
            </IconButton>
        </Tooltip>
    );

    return (
        <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
            <DialogTitle>Configurações da Empresa</DialogTitle>
            <DialogContent>
                <Grid container spacing={3}>
                    {/* Benefícios */}
                    <Grid item xs={12}>
                        <Accordion defaultExpanded>
                            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                                <Typography variant="h6">Benefícios</Typography>
                            </AccordionSummary>
                            <AccordionDetails>
                                <Grid container spacing={2}>
                                    <Grid item xs={12} md={6}>
                                        <TextField
                                            fullWidth
                                            label="Vale Transporte (R$/dia)"
                                            type="number"
                                            value={benefits.valeTransporteDiario}
                                            onChange={(e) => setBenefits({
                                                ...benefits,
                                                valeTransporteDiario: Number(e.target.value)
                                            })}
                                            InputProps={{
                                                endAdornment: renderTooltip(benefitsTooltips.valeTransporte)
                                            }}
                                        />
                                    </Grid>
                                    <Grid item xs={12} md={6}>
                                        <TextField
                                            fullWidth
                                            label="Vale Refeição (R$/dia)"
                                            type="number"
                                            value={benefits.valeRefeicaoDiario}
                                            onChange={(e) => setBenefits({
                                                ...benefits,
                                                valeRefeicaoDiario: Number(e.target.value)
                                            })}
                                            InputProps={{
                                                endAdornment: renderTooltip(benefitsTooltips.valeRefeicao)
                                            }}
                                        />
                                    </Grid>
                                    <Grid item xs={12} md={6}>
                                        <TextField
                                            fullWidth
                                            label="Vale Alimentação (R$/dia)"
                                            type="number"
                                            value={benefits.valeAlimentacaoDiario}
                                            onChange={(e) => setBenefits({
                                                ...benefits,
                                                valeAlimentacaoDiario: Number(e.target.value)
                                            })}
                                            InputProps={{
                                                endAdornment: renderTooltip(benefitsTooltips.valeAlimentacao)
                                            }}
                                        />
                                    </Grid>
                                    <Grid item xs={12} md={6}>
                                        <TextField
                                            fullWidth
                                            label="Auxílio Saúde (R$/mês)"
                                            type="number"
                                            value={benefits.auxilioSaudeMensal}
                                            onChange={(e) => setBenefits({
                                                ...benefits,
                                                auxilioSaudeMensal: Number(e.target.value)
                                            })}
                                            InputProps={{
                                                endAdornment: renderTooltip(benefitsTooltips.auxilioSaude)
                                            }}
                                        />
                                    </Grid>
                                    <Grid item xs={12} md={6}>
                                        <TextField
                                            fullWidth
                                            label="Dias Úteis Média"
                                            type="number"
                                            value={benefits.diasUteisMedia}
                                            onChange={(e) => setBenefits({
                                                ...benefits,
                                                diasUteisMedia: Number(e.target.value)
                                            })}
                                            InputProps={{
                                                endAdornment: renderTooltip(benefitsTooltips.diasUteis)
                                            }}
                                        />
                                    </Grid>
                                    <Grid item xs={12} md={6}>
                                        <TextField
                                            fullWidth
                                            label="Desconto VT (%)"
                                            type="number"
                                            value={benefits.descontoVTPercentual}
                                            onChange={(e) => setBenefits({
                                                ...benefits,
                                                descontoVTPercentual: Number(e.target.value)
                                            })}
                                            InputProps={{
                                                endAdornment: renderTooltip(benefitsTooltips.descontoVT)
                                            }}
                                        />
                                    </Grid>
                                </Grid>
                            </AccordionDetails>
                        </Accordion>
                    </Grid>

                    {/* Encargos */}
                    <Grid item xs={12}>
                        <Accordion defaultExpanded>
                            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                                <Typography variant="h6">Encargos</Typography>
                            </AccordionSummary>
                            <AccordionDetails>
                                <Grid container spacing={2}>
                                    <Grid item xs={12} md={6}>
                                        <TextField
                                            fullWidth
                                            label="INSS Patronal (%)"
                                            type="number"
                                            value={taxConfig.inssPatronal}
                                            onChange={(e) => setTaxConfig({
                                                ...taxConfig,
                                                inssPatronal: Number(e.target.value)
                                            })}
                                            InputProps={{
                                                endAdornment: renderTooltip(taxTooltips.inssPatronal)
                                            }}
                                        />
                                    </Grid>
                                    <Grid item xs={12} md={6}>
                                        <TextField
                                            fullWidth
                                            label="RAT/SAT (%)"
                                            type="number"
                                            value={taxConfig.ratSat}
                                            onChange={(e) => setTaxConfig({
                                                ...taxConfig,
                                                ratSat: Number(e.target.value)
                                            })}
                                            InputProps={{
                                                endAdornment: renderTooltip(taxTooltips.ratSat)
                                            }}
                                        />
                                    </Grid>
                                    <Grid item xs={12} md={6}>
                                        <TextField
                                            fullWidth
                                            label="INSS Terceiros (%)"
                                            type="number"
                                            value={taxConfig.inssTerceiros}
                                            onChange={(e) => setTaxConfig({
                                                ...taxConfig,
                                                inssTerceiros: Number(e.target.value)
                                            })}
                                            InputProps={{
                                                endAdornment: renderTooltip(taxTooltips.inssTerceiros)
                                            }}
                                        />
                                    </Grid>
                                    <Grid item xs={12} md={6}>
                                        <TextField
                                            fullWidth
                                            label="Salário Educação (%)"
                                            type="number"
                                            value={taxConfig.salarioEducacao}
                                            onChange={(e) => setTaxConfig({
                                                ...taxConfig,
                                                salarioEducacao: Number(e.target.value)
                                            })}
                                            InputProps={{
                                                endAdornment: renderTooltip(taxTooltips.salarioEducacao)
                                            }}
                                        />
                                    </Grid>
                                    <Grid item xs={12} md={6}>
                                        <TextField
                                            fullWidth
                                            label="FGTS (%)"
                                            type="number"
                                            value={taxConfig.fgts}
                                            onChange={(e) => setTaxConfig({
                                                ...taxConfig,
                                                fgts: Number(e.target.value)
                                            })}
                                            InputProps={{
                                                endAdornment: renderTooltip(taxTooltips.fgts)
                                            }}
                                        />
                                    </Grid>
                                </Grid>
                            </AccordionDetails>
                        </Accordion>
                    </Grid>

                    {/* Tabelas INSS e IRPF */}
                    <Grid item xs={12}>
                        <Accordion>
                            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                                <Typography variant="h6">Tabelas INSS e IRPF</Typography>
                            </AccordionSummary>
                            <AccordionDetails>
                                <Grid container spacing={3}>
                                    <Grid item xs={12}>
                                        <Typography variant="subtitle1" gutterBottom>
                                            Tabela INSS
                                        </Typography>
                                        <TableContainer component={Paper}>
                                            <Table size="small">
                                                <TableHead>
                                                    <TableRow>
                                                        <TableCell>Faixa</TableCell>
                                                        <TableCell>Alíquota (%)</TableCell>
                                                        <TableCell>Teto (R$)</TableCell>
                                                    </TableRow>
                                                </TableHead>
                                                <TableBody>
                                                    {taxConfig.inssRanges.map((range, index) => (
                                                        <TableRow key={index}>
                                                            <TableCell>{range.faixa}</TableCell>
                                                            <TableCell>
                                                                <TextField
                                                                    type="number"
                                                                    value={range.aliquota}
                                                                    onChange={(e) => {
                                                                        const newRanges = [...taxConfig.inssRanges];
                                                                        newRanges[index].aliquota = Number(e.target.value);
                                                                        setTaxConfig({
                                                                            ...taxConfig,
                                                                            inssRanges: newRanges
                                                                        });
                                                                    }}
                                                                    size="small"
                                                                />
                                                            </TableCell>
                                                            <TableCell>
                                                                <TextField
                                                                    type="number"
                                                                    value={range.tetoFaixa}
                                                                    onChange={(e) => {
                                                                        const newRanges = [...taxConfig.inssRanges];
                                                                        newRanges[index].tetoFaixa = Number(e.target.value);
                                                                        setTaxConfig({
                                                                            ...taxConfig,
                                                                            inssRanges: newRanges
                                                                        });
                                                                    }}
                                                                    size="small"
                                                                />
                                                            </TableCell>
                                                        </TableRow>
                                                    ))}
                                                </TableBody>
                                            </Table>
                                        </TableContainer>
                                    </Grid>

                                    <Grid item xs={12}>
                                        <Typography variant="subtitle1" gutterBottom>
                                            Tabela IRPF
                                        </Typography>
                                        <TableContainer component={Paper}>
                                            <Table size="small">
                                                <TableHead>
                                                    <TableRow>
                                                        <TableCell>Faixa</TableCell>
                                                        <TableCell>Alíquota (%)</TableCell>
                                                        <TableCell>Dedução (R$)</TableCell>
                                                        <TableCell>Teto (R$)</TableCell>
                                                    </TableRow>
                                                </TableHead>
                                                <TableBody>
                                                    {taxConfig.irpfRanges.map((range, index) => (
                                                        <TableRow key={index}>
                                                            <TableCell>{range.faixa}</TableCell>
                                                            <TableCell>
                                                                <TextField
                                                                    type="number"
                                                                    value={range.aliquota}
                                                                    onChange={(e) => {
                                                                        const newRanges = [...taxConfig.irpfRanges];
                                                                        newRanges[index].aliquota = Number(e.target.value);
                                                                        setTaxConfig({
                                                                            ...taxConfig,
                                                                            irpfRanges: newRanges
                                                                        });
                                                                    }}
                                                                    size="small"
                                                                />
                                                            </TableCell>
                                                            <TableCell>
                                                                <TextField
                                                                    type="number"
                                                                    value={range.deducao}
                                                                    onChange={(e) => {
                                                                        const newRanges = [...taxConfig.irpfRanges];
                                                                        newRanges[index].deducao = Number(e.target.value);
                                                                        setTaxConfig({
                                                                            ...taxConfig,
                                                                            irpfRanges: newRanges
                                                                        });
                                                                    }}
                                                                    size="small"
                                                                />
                                                            </TableCell>
                                                            <TableCell>
                                                                <TextField
                                                                    type="number"
                                                                    value={range.tetoFaixa}
                                                                    onChange={(e) => {
                                                                        const newRanges = [...taxConfig.irpfRanges];
                                                                        newRanges[index].tetoFaixa = Number(e.target.value);
                                                                        setTaxConfig({
                                                                            ...taxConfig,
                                                                            irpfRanges: newRanges
                                                                        });
                                                                    }}
                                                                    size="small"
                                                                    disabled={index === taxConfig.irpfRanges.length - 1}
                                                                />
                                                            </TableCell>
                                                        </TableRow>
                                                    ))}
                                                </TableBody>
                                            </Table>
                                        </TableContainer>
                                    </Grid>
                                </Grid>
                            </AccordionDetails>
                        </Accordion>
                    </Grid>
                </Grid>
            </DialogContent>
            <DialogActions>
                <Button onClick={onClose}>Cancelar</Button>
                <Button onClick={handleSave} variant="contained" color="primary">
                    Salvar
                </Button>
            </DialogActions>
        </Dialog>
    );
}; 